import { Callback, Inventory, ItemDetailMeta, WeaponCharacteristics } from "game-common/models"
import { Mechanics } from "game-common/mechanics/mechanics"
import { Container, Graphics, InteractionManager, LINE_JOIN, Point, Rectangle, Text } from "pixi.js"

import { SpriteMeta } from "../entity_rendering_manager"
import { GuiManager } from "../gui_manager"
import { BasicSprite } from "../sprites/basic_sprite"
import { centerSpriteIn } from "./center_sprite"
import { WearBar } from "./durability"
import { TextRenderer } from "../text/text_renderer"
import { disposeTextTextures } from "../text/dispose_text"
import { ItemSlotDrag } from "./item_slot_drag"

// The readied-item tray, bottom-left.
//
// It used to be four loose sprites lying on the grass: no frame, the shortcut digit floating up and
// to the left of its item, the quantity printed straight over the art, and the EQUIPPED item tinted
// 0x00ff00 -- which turned the one item you most need to recognise into a green silhouette. This
// draws a tray instead: a sheer dark plate binding a row of wells, each well a fixed box whatever is
// sitting in it, with armed state carried by an amber rim rather than by repainting the art.
//
// Palette and glass are the ember HUD's, deliberately shared with gui/chat_theme.ts -- the chat log
// sits directly above this in the same corner, and the two have to read as one stack rather than two
// overlays from different games.

// ---- metrics -------------------------------------------------------------------------------
// A well is a fixed 40 whatever the item is, and the item is normalised INTO it (see renderSlot):
// the art in this inventory runs from 18x14 to 32x32, so sized-to-content slots came out ragged,
// with no shared baseline and no two press targets the same size.
export const SLOT = 40
export const SLOT_GAP = 5
export const SLOT_PITCH = SLOT + SLOT_GAP
export const TRAY_PAD = 4
// Fixed, and exported, because the widget is EMPTY until the first inventory arrives: measuring it
// at construction time to anchor it off the bottom of the screen (what gui_manager used to do) reads
// 0 and lands the tray a slot too low. A constant is the honest answer -- the tray's height does not
// depend on its contents.
export const TRAY_HEIGHT = TRAY_PAD * 2 + SLOT

const TRAY_CORNER = 8
const WELL_CORNER = 5
// Art is scaled DOWN into this box and never up: 32px tile art then still draws at 1:1, which keeps
// pixel art crisp, and only the oversized stragglers are touched.
const ICON_BOX = 32
// The ammo icon is budgeted by WIDTH, not by its longest side. An arrow is naturally 27x5 -- a long
// thin thing -- so scaling it by the longest side to fit a square box took it to 16x3, a hairline
// that read as a scratch on the plate. Against a width budget it keeps its native 1:1 size.
const AMMO_ICON_W = 28

// ---- palette (gui/chat_theme.ts) -------------------------------------------------------------
const TRAY_FILL = 0x1b1512
// Sheer on purpose. The wells carry the contrast; the plate only has to bind them into one object,
// and at the old panel alpha it stamped a black bar across the bottom of the world.
const TRAY_ALPHA = 0.4
const SHADOW_ALPHA = 0.16
const RIM = 0x6b4f38
const RIM_ALPHA = 0.7
const WELL_FILL = 0x140f0c
const WELL_ALPHA = 0.9
const WELL_FILL_ARMED = 0x352619
const ARMED = 0xf2a94e
const SELECTION_SLIDE_MS = 180
const BADGE_FILL = 0x0a0706
const TEXT_COLOR = 0xfff3e0
const KEY_COLOR = 0xa89480

// A stack of 97995 logs printed in full was wider than the slot pitch and ran straight over its
// neighbour. Past four digits the exact number is not what the glance is for.
export const compactQuantity = (n: number): string => {
    if (n < 10000) {
        return `${n}`
    }
    const thousands = Math.round(n / 1000)
    return thousands < 1000 ? `${thousands}k` : "999k+"
}

export interface AmmoReadout {
    icon: string
    quantity: number
}

export interface ReadiedItemDragOptions {
    interaction: InteractionManager
    layer: Container
    isWorldDrop: (point: Point) => boolean
    drop: (itemId: string) => void
}

export class EquippedWidget extends Graphics {
    guiManager: GuiManager
    private itemDrag?: ItemSlotDrag
    private woodSlotBounds?: Rectangle
    private dropSlots: { node: Graphics; item: ItemDetailMeta }[] = []

    // Inventory drags use the same server-issued Ready/Replace actions as the panel's ready row.
    registerInventoryDropTarget = (drag: ItemSlotDrag) => {
        const width = TRAY_PAD * 2 + Math.max(1, this.dropSlots.length) * SLOT_PITCH - SLOT_GAP
        const feedback = new Graphics()
        feedback.interactive = false
        feedback.lineStyle(2, ARMED, 1)
        feedback.beginFill(WELL_FILL_ARMED, 0.5)
        feedback.drawRoundedRect(0, 0, width, TRAY_HEIGHT, WELL_CORNER)
        feedback.endFill()
        drag.registerSection(this, new Rectangle(0, 0, width, TRAY_HEIGHT), "readied", on => {
            if (on && this.worldVisible) this.addChild(feedback)
            else feedback.parent?.removeChild(feedback)
        })
        this.dropSlots.forEach(({ node, item }) => {
            drag.registerTarget(node, item, "readied", SLOT, () => {})
        })
    }
    // The gauges with something to animate -- i.e. the weapons close enough to breaking to be
    // warning about. Almost always empty, which is what makes `animate` free to call every frame.
    private wearBars: WearBar[] = []
    private selectionRing?: Graphics
    private selectionFromX = 0
    private selectionTargetX = 0
    private selectionElapsed = SELECTION_SLIDE_MS

    getWoodSlotBounds = (): Rectangle | undefined => this.woodSlotBounds

    constructor(guiManager: GuiManager, private dragOptions?: ReadiedItemDragOptions) {
        super()

        this.guiManager = guiManager
        this.on("removed", () => this.itemDrag?.cancel())
    }

    // One slot. Its origin is the well's TOP-LEFT, so the widget's bounds are the tray's bounds and
    // the thing can be anchored by arithmetic instead of by measurement.
    renderSlot = (label: string, spriteMeta: SpriteMeta, selected: boolean, callback: Callback, amt?: number,
        condition?: number) => {
        const slot = new Graphics()

        slot.beginFill(selected ? WELL_FILL_ARMED : WELL_FILL, WELL_ALPHA)
        slot.lineStyle({
            width: 1,
            color: RIM,
            alpha: 0.85,
            join: LINE_JOIN.ROUND,
        })
        slot.drawRoundedRect(0.5, 0.5, SLOT - 1, SLOT - 1, WELL_CORNER)
        slot.endFill()

        slot.lineStyle(0)

        // The item is NOT tinted by its armed state. That was the single worst thing about the old
        // tray: `color: item.active ? 0x00ff00` repainted the equipped item as a flat green blob, so
        // the one slot you most need to identify was the one you could not read. Armed is the rim's
        // job.
        const item = new BasicSprite(spriteMeta)
        const art = item.getLocalBounds()
        const longest = Math.max(art.width, art.height)
        if (longest > ICON_BOX) {
            item.scale.set(ICON_BOX / longest)
        }
        centerSpriteIn(item, 0, 0, SLOT)
        slot.addChild(item)

        // Shortcut digit, in the well's corner rather than floating above it. It is a hint, not a
        // headline, so it is dim until the slot is armed.
        const labelObject = new TextRenderer()
            .size("tiny")
            .color(selected ? ARMED : KEY_COLOR)
            .render(label) as Text
        labelObject.x = 3
        labelObject.y = 1
        slot.addChild(labelObject)

        let badgeInset = 0
        if (amt !== undefined) {
            const amtLabel = new TextRenderer()
                .size("tiny")
                .color(TEXT_COLOR)
                .strokeThickness(0)
                .render(compactQuantity(amt)) as Text
            const badgeW = amtLabel.width + 5
            const badgeH = amtLabel.height + 1
            const badgeX = SLOT - badgeW - 2
            const badgeY = SLOT - badgeH - 2
            // The plate goes on its OWN Graphics, added after the item, rather than into the slot's
            // geometry: a Container draws its own geometry first and its children over it, so a badge
            // drawn into the slot would sit UNDER the art it is supposed to be legible against -- and
            // the number, being a child, would have floated over bare item art.
            const badge = new Graphics()
            badge.beginFill(BADGE_FILL, 0.92)
            badge.drawRoundedRect(badgeX, badgeY, badgeW, badgeH, 3)
            badge.endFill()
            amtLabel.x = badgeX + 2.5
            amtLabel.y = badgeY + 0.5
            badge.addChild(amtLabel)
            slot.addChild(badge)
            // Measured, not guessed: how far in from the right the wear gauge has to stop so the
            // two never overlap, whatever the count is.
            badgeInset = SLOT - badgeX + 2
        }

        // The wear gauge, if this thing wears and has started to. Added LAST, for the badge's reason
        // and then some: it sits in the bottom 8px of the well, which is exactly where a 32px icon
        // in a 40px box reaches, so drawn into the slot's own geometry it would be under the art.
        const wear = WearBar.forSlot(condition, SLOT, badgeInset)
        if (wear) {
            slot.addChild(wear)
            this.wearBars.push(wear)
        }

        // One uniform press target per slot, rather than each item's own sprite bounds.
        //
        // Hanging the handler off the sprite made a slot exactly as easy to hit as its art is big: a
        // shield filled the slot and a dagger was a few pixels of blade on the diagonal, so the same
        // gesture picked one reliably and missed the other. Which item is in a slot should not change
        // how hard it is to select.
        //
        // It is exactly one pitch wide and the full height of the tray -- the largest target that
        // cannot overlap its neighbour and steal its taps -- so the gaps between wells are live too,
        // and a thumb landing between two slots still gets the nearer one.
        slot.interactive = true
        slot.cursor = "pointer"
        slot.hitArea = new Rectangle(-SLOT_GAP / 2, -TRAY_PAD, SLOT_PITCH, TRAY_HEIGHT)
        slot.on("pointertap", event => {
            if (event?.data?.pointerType !== "touch" && event?.data?.button > 0) return
            if (!this.itemDrag?.suppressClick(slot)) callback()
        })

        return slot
    }

    // The arrows (or whatever the readied ranged weapon eats) docked at the right-hand end of the
    // tray. The count used to live in the ResourceBar beside the player's name in the TOP-LEFT
    // corner, a whole screen away from the bow it belongs to.
    private renderAmmo = (ammo: AmmoReadout, x: number): { node: Graphics; width: number } => {
        const node = new Graphics()
        node.x = x

        node.lineStyle({ width: 1, color: RIM, alpha: RIM_ALPHA })
        node.moveTo(0.5, TRAY_PAD + 6)
        node.lineTo(0.5, TRAY_HEIGHT - TRAY_PAD - 6)
        node.lineStyle(0)

        const icon = new BasicSprite({ animated: false, color: 0xffffff, name: ammo.icon, scale: 1 })
        const art = icon.getLocalBounds()
        if (art.width > AMMO_ICON_W) {
            icon.scale.set(AMMO_ICON_W / art.width)
        }
        // Centred in a square the width of the budget, so the icon sits on the tray's middle line
        // whatever shape the ammo happens to be.
        centerSpriteIn(icon, 6, (TRAY_HEIGHT - AMMO_ICON_W) / 2, AMMO_ICON_W)
        node.addChild(icon)

        const count = new TextRenderer()
            .role("compactCaption")
            .bold()
            .color(TEXT_COLOR)
            .render(compactQuantity(ammo.quantity)) as Text
        count.x = 6 + AMMO_ICON_W + 4
        count.y = (TRAY_HEIGHT - count.height) / 2
        node.addChild(count)

        return { node, width: count.x + count.width }
    }

    private drawTray = (width: number) => {
        this.clear()

        if (width <= 0) {
            return
        }

        // Fake drop shadow: an offset copy behind the plate, no filters. Kept faint because the
        // plate above it is sheer and would otherwise read as mottled rather than shadowed.
        this.beginFill(0x000000, SHADOW_ALPHA)
        this.drawRoundedRect(0, 2, width, TRAY_HEIGHT, TRAY_CORNER)
        this.endFill()

        this.beginFill(TRAY_FILL, TRAY_ALPHA)
        this.lineStyle({ width: 1, color: RIM, alpha: RIM_ALPHA, join: LINE_JOIN.ROUND })
        this.drawRoundedRect(0.5, 0.5, width - 1, TRAY_HEIGHT - 1, TRAY_CORNER)
        this.endFill()
        this.lineStyle(0)
    }

    private moveSelectionRing = (x?: number) => {
        if (x === undefined) {
            if (this.selectionRing) this.selectionRing.visible = false
            this.selectionElapsed = SELECTION_SLIDE_MS
            return
        }

        if (!this.selectionRing) {
            const ring = this.selectionRing = new Graphics()
            ring.interactive = false
            ring.lineStyle({ width: 1.5, color: ARMED, alpha: 1, join: LINE_JOIN.ROUND })
            ring.drawRoundedRect(0.5, 0.5, SLOT - 1, SLOT - 1, WELL_CORNER)
            // Inset glow without a filter or an extra render target.
            ring.lineStyle({ width: 3, color: ARMED, alpha: 0.18, join: LINE_JOIN.ROUND })
            ring.drawRoundedRect(2.5, 2.5, SLOT - 5, SLOT - 5, Math.max(1, WELL_CORNER - 2))
            ring.visible = false
        }

        const ring = this.selectionRing
        if (!ring.visible) {
            ring.position.set(x, TRAY_PAD)
            this.selectionTargetX = x
            this.selectionElapsed = SELECTION_SLIDE_MS
        } else if (x !== this.selectionTargetX) {
            this.selectionFromX = ring.x
            this.selectionTargetX = x
            this.selectionElapsed = 0
        }
        ring.visible = true
        this.addChild(ring)
    }

    update = (
        inventory: Inventory,
        slotCallback: (itemId: string) => void,
        ammo?: AmmoReadout,
    ): Record<number, Callback> => {
        this.itemDrag?.cancel()
        this.itemDrag = undefined
        // clear all -- dispose per-instance Text textures first, else slot labels/quantities leak a
        // canvas texture each on every inventory update (this rebuilds constantly during gameplay as
        // items are gathered/equipped). Shared item-sprite textures are left intact.
        ;[...this.children].forEach(disposeTextTextures)
        this.removeChildren()
        this.woodSlotBounds = undefined
        this.dropSlots = []
        this.wearBars = []

        if (this.dragOptions) {
            const { interaction, layer, isWorldDrop, drop } = this.dragOptions
            this.itemDrag = new ItemSlotDrag(interaction, layer,
                item => this.renderSlot("", { animated: false, color: 0xffffff, name: item.spriteId, scale: 1 },
                    false, () => {}, item.quantity > 1 ? item.quantity : undefined, item.condition),
                item => drop(item.id!), () => {}, isWorldDrop)
        }

        const baseMeta: SpriteMeta = {
            animated: false,
            color: 0xffffff,
            name: "",
            scale: 1.0,
        }

        const slotCallbacks: Record<number, Callback> = {}

        const readied = (inventory.items || []).filter(
            item => item.readied && (WeaponCharacteristics[item.itemType]?.slotVisible ||
                Mechanics.item.properties.primaryUseable(item.itemTypeMeta)),
        )
        const selectedIndex = readied.findIndex(item => item.active)

        readied.forEach((item, i) => {
            const name = item.itemTypeMeta.spriteId || `${item.itemTypeMeta.type}-${item.itemTypeMeta.color}.png`
            const shortcutKey = `${i + 1}`
            const callback = () => slotCallback(item.id)
            // Computed here rather than sent: the whole inventory already rides to the client, hp and
            // all, so the tray needs nothing new from the server to know the axe in slot 1 is spent.
            const condition = Mechanics.item.durability.computeItemConditionPct(item)

            const slot = this.renderSlot(
                shortcutKey,
                { ...baseMeta, name },
                item.active,
                callback,
                // A bare "1" on every single weapon is noise; a count is only news when there is
                // more than one of the thing.
                item.quantity > 1 ? item.quantity : undefined,
                condition,
            )

            slotCallbacks[i + 1] = callback

            slot.x = TRAY_PAD + i * SLOT_PITCH
            slot.y = TRAY_PAD
            if (item.itemTypeMeta.type === "wood" && item.quantity >= 1 && !this.woodSlotBounds) {
                this.woodSlotBounds = new Rectangle(slot.x, slot.y, SLOT, SLOT)
            }
            this.addChild(slot)
            const detail: ItemDetailMeta = { id: item.id, title: item.flags?.chargedWeaponId ? item.description || item.flags.chargedWeaponId : item.itemTypeMeta.type, spriteId: name,
                quantity: item.quantity, options: [], condition,
                itemDropOptions: { world: { title: "Drop", onClickContext: {} } } }
            this.dropSlots.push({ node: slot, item: detail })
            this.itemDrag?.register(slot, detail, "readied", SLOT, () => {})
        })

        // No readied items, no tray. An empty plate on the grass is just a smudge.
        if (readied.length === 0) {
            this.moveSelectionRing()
            this.drawTray(0)
            return slotCallbacks
        }

        let width = TRAY_PAD + (readied.length - 1) * SLOT_PITCH + SLOT + TRAY_PAD

        if (ammo) {
            const { node, width: ammoWidth } = this.renderAmmo(ammo, width - TRAY_PAD + 1)
            this.addChild(node)
            width = node.x + ammoWidth + TRAY_PAD + 2
        }

        this.drawTray(width)
        this.moveSelectionRing(selectedIndex < 0 ? undefined : TRAY_PAD + selectedIndex * SLOT_PITCH)

        return slotCallbacks
    }

    /**
     * Slide the selection ring and breathe gauges of weapons about to break.
     *
     * The tray is rebuilt only when the inventory changes, so this is its only frame clock -- and it
     * only animates while the ring is moving or a weapon is in the warning tier, so
     * gui_manager can call it unconditionally. A fragile weapon is the one wear state that has to
     * reach a player who is looking at the fight rather than at the tray.
     */
    animate = (deltaMs: number) => {
        if (this.selectionRing?.visible && this.selectionElapsed < SELECTION_SLIDE_MS) {
            this.selectionElapsed = Math.min(SELECTION_SLIDE_MS, this.selectionElapsed + Math.max(0, deltaMs))
            const progress = this.selectionElapsed / SELECTION_SLIDE_MS
            const eased = 1 - Math.pow(1 - progress, 3)
            this.selectionRing.x = this.selectionFromX + (this.selectionTargetX - this.selectionFromX) * eased
        }
        this.wearBars.forEach(bar => bar.animate(deltaMs))
    }

    destroy(options?: Parameters<Graphics["destroy"]>[0]) {
        this.itemDrag?.cancel()
        this.selectionRing?.destroy()
        super.destroy(options)
    }
}
